/**
 * Pure reflow planner: lay a running focus out again in the current surface
 * (a window resize, the end of a native geometry transition, a fit request)
 * while keeping its scripted layouts and the user's window modes.
 */
import { calculateConversationFocusBoxes } from "@/lib/conversation-focus-layout-geometry";
import {
  calculateConversationCompanionFocusLayout,
  scaleConversationCompanionLayoutToSurface,
} from "@/lib/conversation-companion-focus-geometry";
import { type FocusWindowBox } from "@/lib/focus-window-layout";
import { calculateProportionalNormalBox } from "@/lib/focus/focus-container-boxes";
import type {
  FocusNaturalSize,
  PresentationState,
} from "@/lib/focus/focus-types";
import {
  focusBoxesMatch,
  isConversationFrame,
  planFocusBoxes,
  planFocusWindow,
  type FocusPlanEnv,
} from "@/lib/focus/focus-plan-layout";
import { planRoundLayout } from "@/lib/focus/focus-plan-rounds";

export interface FocusReflowInput {
  /** The agent content fit's box in the current surface, when it has one. */
  contentFitBox?: FocusWindowBox;
}

export function planFocusReflow(
  state: PresentationState,
  input: FocusReflowInput,
  env: FocusPlanEnv,
): PresentationState | null {
  const activeId = state.frameIds[state.activeIndex] ?? state.frameIds[0];
  if (state.variant !== "focus" || !activeId) return null;
  const { surface } = env;
  const withBoxes = (next: PresentationState, boxes: FocusWindowBox[]) =>
    planFocusBoxes(
      next,
      activeId,
      { boxes, preserveCustomFocusLayout: true },
      env,
    );

  if (state.frameIds.every((id) => isConversationFrame(env, id))) {
    const boxes = calculateConversationFocusBoxes(
      surface,
      state.frameIds.length,
    );
    return withBoxes(
      {
        ...state,
        agentContentFit: undefined,
        focusWindow: undefined,
        customFocusLayout: { kind: "conversation_columns", boxes },
      },
      boxes,
    );
  }

  const layout = state.customFocusLayout;
  if (layout?.kind === "conversation_companion_stack") {
    const nextLayout = layout.userAdjusted
      ? scaleConversationCompanionLayoutToSurface(
          layout,
          surface,
          state.frameIds,
        )
      : calculateConversationCompanionFocusLayout(
          surface,
          state.frameIds,
          layout.conversationFrameId,
          layout.expandedStackFrameId,
        );
    return withBoxes(
      {
        ...state,
        agentContentFit: undefined,
        focusWindow: undefined,
        customFocusLayout: nextLayout,
      },
      nextLayout.boxes,
    );
  }

  // Several frames, or a single one left of them: the main keeps its slot
  // while the surface and its size stay.
  if (layout?.kind === "rounds" || state.frameIds.length > 1) {
    return planRoundLayout(state, env);
  }

  if (state.agentContentFit?.frameId === activeId && input.contentFitBox) {
    return planFocusBoxes(
      state,
      activeId,
      { boxes: [input.contentFitBox] },
      env,
    );
  }

  const mode = state.focusWindow?.mode ?? "normal";
  return planFocusWindow(
    state,
    activeId,
    mode,
    mode === "normal" || !state.focusWindow
      ? {}
      : { normalBox: state.focusWindow.normalBox },
    env,
  );
}

/** Whether a reflow leaves the presentation exactly as it was. */
export function reflowChangesNothing(
  previous: PresentationState,
  next: PresentationState,
): boolean {
  return JSON.stringify(previous) === JSON.stringify(next);
}

/**
 * A card-only frame's size once its detail landed: the layout follows it
 * unless the user already adjusted it or an agent fitted its content.
 */
export function planNaturalSizeRefresh(
  state: PresentationState,
  landed: Record<string, FocusNaturalSize>,
  env: FocusPlanEnv,
): PresentationState {
  const window = state.focusWindow;
  const windowLanded = window?.mode === "normal" && !!landed[window.frameId];
  const windowMoved =
    windowLanded &&
    !focusBoxesMatch(
      window.normalBox,
      calculateProportionalNormalBox(
        state.surface ?? env.surface,
        state.naturalSizes[window.frameId],
      ),
    );
  const layout = state.customFocusLayout;
  const userAdjusted =
    windowMoved ||
    (layout?.kind === "conversation_companion_stack" && !!layout.userAdjusted);
  if (userAdjusted || state.agentContentFit) {
    const settled = Object.fromEntries(
      Object.entries(state.naturalSizes).map(([frameId, size]) => {
        if (!landed[frameId]) return [frameId, size];
        const { provisional: _provisional, ...kept } = size;
        return [frameId, kept];
      }),
    );
    return { ...state, naturalSizes: settled };
  }
  const refreshed: PresentationState = {
    ...state,
    naturalSizes: { ...state.naturalSizes, ...landed },
    focusWindow: windowLanded ? undefined : window,
  };
  return planFocusReflow(refreshed, {}, env) ?? refreshed;
}
